import { useState, useMemo, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, Pill, Btn } from '../components/forecast/prims'
import { C, F } from '../components/forecast/theme'
import { useEvents, useEventTypes, useMeracSummary, useMergeEvents } from '../hooks/useEvents'
import { useUsers } from '../hooks/useUsers'
import { useMe } from '../hooks/useMe'
import { formatEventDates } from '../lib/dates'
import { clampProgress } from '../lib/progress'
import { NewEventModal } from '../components/NewEventModal'
import { Icon } from '../components/Icon'
import { FilterPill } from '../components/FilterPill'
import {
  loadFilterSets, saveFilterSets, newFilterSetId,
  type SavedFilterSet, type FilterSetStore
} from '../lib/eventFilterSets'
import type { EventListItem, EventStatus, UserOption, EventType, EventMeracSummary, MeracSummaryMap } from '../types'

// EventsList - the master list of events. v2 of this page adds:
//   - Expandable Filters panel (Type chip group + Manager dropdown + date range)
//   - Grid / List view toggle (component state only; resets on page navigation)
//
// The status tabs and search input stay where they were so muscle memory
// from the v1 list doesn't get reorganised. Filters layer on top of those.

type ViewMode = 'grid' | 'list'

const STATUS_FILTERS: ('All' | EventStatus)[] = [
  'All', 'Idea', 'Draft', 'In Development', 'Live', 'Complete'
]

// Tab label — 'Idea' status surfaces as the "Ideas" tab (Forecast Studio entry).
function statusTabLabel(s: 'All' | EventStatus): string {
  return s === 'Idea' ? 'Ideas' : s
}

interface Filters {
  typeIds: Set<number>
  managerId: number | 'all'
  fromDate: string
  toDate: string
}

const EMPTY_FILTERS: Filters = {
  typeIds: new Set(),
  managerId: 'all',
  fromDate: '',
  toDate: ''
}

// Local "today" as YYYY-MM-DD (browser-local; NYMR runs UK time).
function localTodayIso(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// An event is treated as Complete once its last date is in the past, REGARDLESS
// of its stored status — finished events (often still flagged Live / In
// Development) otherwise clutter the active tabs. endDate is the envelope max of
// the (multi-)date set, so a strict `<` keeps events whose final day is today
// still visible. Past + genuinely-Complete events surface under the Complete tab.
function effectiveStatus(e: EventListItem, todayIso: string): EventStatus {
  // Ideas are pre-plan candidates — never reclassify them by date.
  if (e.status === 'Idea') return 'Idea'
  return e.endDate < todayIso ? 'Complete' : e.status
}

export function EventsList() {
  const { data: events, isLoading, error } = useEvents()
  const { data: types } = useEventTypes()
  const { data: users } = useUsers()
  const { data: meracSummary } = useMeracSummary()

  const [statusFilter, setStatusFilter] = useState<'All' | EventStatus>('All')
  const [search, setSearch] = useState('')
  const [view, setView] = useState<ViewMode>('grid')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [creatingNew, setCreatingNew] = useState(false)
  const [creatingIdea, setCreatingIdea] = useState(false)
  const [merging, setMerging] = useState(false)
  // Tweak T2b — archived events are hidden by default; toggle pill
  // brings them back, rendered faded so the visual contrast with
  // active events is preserved.
  const [showArchived, setShowArchived] = useState(false)

  // Saved filter sets (item 16). Stored per-user in localStorage; the default
  // set (if any) is applied once on first load.
  const { data: me } = useMe()
  const [store, setStore] = useState<FilterSetStore>({ sets: [], defaultId: null })
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null)
  const appliedDefault = useRef(false)

  function applySet(s: SavedFilterSet) {
    setStatusFilter(s.statusFilter)
    setView(s.view)
    setShowArchived(s.showArchived)
    setFilters({
      typeIds: new Set(s.typeIds),
      managerId: s.managerId,
      fromDate: s.fromDate,
      toDate: s.toDate
    })
  }

  useEffect(() => {
    if (me?.dbUserId == null) return
    const loaded = loadFilterSets(me.dbUserId)
    setStore(loaded)
    if (!appliedDefault.current && loaded.defaultId) {
      const def = loaded.sets.find((s) => s.id === loaded.defaultId)
      if (def) {
        applySet(def)
        setSelectedSetId(def.id)
      }
      appliedDefault.current = true
    }
  }, [me?.dbUserId])

  function persistStore(next: FilterSetStore) {
    setStore(next)
    saveFilterSets(me?.dbUserId, next)
  }
  function snapshotCurrent(): Omit<SavedFilterSet, 'id' | 'name'> {
    return {
      statusFilter, view, showArchived,
      typeIds: [...filters.typeIds],
      managerId: filters.managerId,
      fromDate: filters.fromDate,
      toDate: filters.toDate
    }
  }
  function applySetById(id: string) {
    const s = store.sets.find((x) => x.id === id)
    if (!s) return
    applySet(s)
    setSelectedSetId(id)
  }
  function saveCurrentAsNew() {
    const name = window.prompt('Name this filter set:')?.trim()
    if (!name) return
    const set: SavedFilterSet = { id: newFilterSetId(), name, ...snapshotCurrent() }
    persistStore({ ...store, sets: [...store.sets, set] })
    setSelectedSetId(set.id)
  }
  function updateSelectedSet() {
    if (!selectedSetId) return
    persistStore({
      ...store,
      sets: store.sets.map((s) => (s.id === selectedSetId ? { ...s, ...snapshotCurrent() } : s))
    })
  }
  function deleteSet(id: string) {
    persistStore({
      sets: store.sets.filter((s) => s.id !== id),
      defaultId: store.defaultId === id ? null : store.defaultId
    })
    if (selectedSetId === id) setSelectedSetId(null)
  }
  function toggleDefault(id: string) {
    persistStore({ ...store, defaultId: store.defaultId === id ? null : id })
  }
  // One-click "make this my default view": snapshot the current filters/sort/view
  // and store them as the default set that's auto-applied when the list loads.
  // Updates the existing default in place, or creates a "My default view" set.
  function saveCurrentAsDefault() {
    const snap = snapshotCurrent()
    const existing = store.defaultId ? store.sets.find((s) => s.id === store.defaultId) : null
    if (existing) {
      persistStore({
        ...store,
        sets: store.sets.map((s) => (s.id === existing.id ? { ...s, ...snap } : s))
      })
      setSelectedSetId(existing.id)
    } else {
      const set: SavedFilterSet = { id: newFilterSetId(), name: 'My default view', ...snap }
      persistStore({ sets: [...store.sets, set], defaultId: set.id })
      setSelectedSetId(set.id)
    }
  }
  const defaultSetName =
    store.defaultId ? store.sets.find((s) => s.id === store.defaultId)?.name ?? null : null

  // Stable for the life of the mounted list; recomputed on remount.
  const todayIso = useMemo(() => localTodayIso(), [])

  const filtered = useMemo(() => {
    const list = events ?? []
    return list.filter((e) => {
      if (!showArchived && e.archivedAt != null) return false
      const eff = effectiveStatus(e, todayIso)
      if (statusFilter === 'All') {
        // Default view = current + upcoming work only. Past-dated and
        // genuinely-Complete events roll into the Complete tab; Ideas live in
        // their own tab (pre-plan candidates, not on the operating programme).
        if (eff === 'Complete' || eff === 'Idea') return false
      } else if (eff !== statusFilter) {
        return false
      }
      if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false
      if (filters.typeIds.size > 0 && !filters.typeIds.has(e.type.id)) return false
      if (filters.managerId !== 'all') {
        if (filters.managerId === 0) {
          if (e.manager) return false
        } else if (e.manager?.id !== filters.managerId) {
          return false
        }
      }
      if (filters.fromDate && e.endDate < filters.fromDate) return false
      if (filters.toDate && e.startDate > filters.toDate) return false
      return true
    })
  }, [events, showArchived, statusFilter, search, filters, todayIso])

  const archivedCount = useMemo(
    () => (events ?? []).filter((e) => e.archivedAt != null).length,
    [events]
  )

  // Same-named duplicate groups (item 8) — non-archived events sharing a name,
  // 2+ in a group. Drives the "Merge duplicates" tool.
  const duplicateGroups = useMemo(() => {
    const byName = new Map<string, EventListItem[]>()
    for (const e of events ?? []) {
      if (e.archivedAt != null) continue
      const key = e.name.trim().toLowerCase()
      const arr = byName.get(key) ?? []
      arr.push(e)
      byName.set(key, arr)
    }
    return Array.from(byName.values())
      .filter((g) => g.length >= 2)
      .map((g) => [...g].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.id - b.id))
  }, [events])

  const activeFilterCount =
    filters.typeIds.size +
    (filters.managerId !== 'all' ? 1 : 0) +
    (filters.fromDate ? 1 : 0) +
    (filters.toDate ? 1 : 0)

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Events</h1>
          <p className="text-ink-soft">
            {events ? `${events.length} events in the 2026 programme` : ' '}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 text-sm bg-white border border-line rounded outline-none focus:border-green w-56"
          />
          {archivedCount > 0 && (
            <FilterPill
              label="Show archived"
              count={archivedCount}
              active={showArchived}
              onClick={() => setShowArchived((v) => !v)}
            />
          )}
          <ViewToggle view={view} onChange={setView} />
          <a
            href="/events-report"
            target="_blank"
            rel="noopener noreferrer"
            title="All current events as a printable PDF list — use the browser's Save as PDF."
            className="px-3 py-2 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors whitespace-nowrap"
          >
            Report
          </a>
          {me?.isAdmin && duplicateGroups.length > 0 && (
            <button
              type="button"
              onClick={() => setMerging(true)}
              title="Some events share a name — merge each set into one multi-date event"
              className="px-3 py-2 text-sm font-semibold rounded border border-brass text-brass hover:bg-brass-soft transition-colors whitespace-nowrap"
            >
              Merge duplicates ({duplicateGroups.length})
            </button>
          )}
          <button
            type="button"
            onClick={() => setCreatingIdea(true)}
            title="Model a candidate event for viability in Forecast Studio before committing it to the plan."
            className="px-4 py-2 text-sm font-semibold rounded border border-brass bg-brass-soft text-brass hover:bg-brass/15 transition-colors whitespace-nowrap"
          >
            + New idea
          </button>
          <button
            type="button"
            onClick={() => setCreatingNew(true)}
            className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors whitespace-nowrap"
          >
            + New event
          </button>
        </div>
      </div>

      {/* Saved filter sets (item 16) — apply a saved set, save the current
          filters as a new named set, or mark one as the default that's
          applied automatically when the list opens. */}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
          Saved filters
        </span>
        <button
          type="button"
          onClick={saveCurrentAsDefault}
          title="Remember the current filters, sort and view as the default you land on every time you open Events."
          className="px-2.5 py-1.5 text-xs font-semibold rounded border border-brass text-brass bg-brass-soft hover:bg-brass/15 transition-colors"
        >
          ★ Save current view as my default
        </button>
        {defaultSetName && (
          <span className="text-xs text-ink-soft">
            Default: <span className="font-semibold text-ink">{defaultSetName}</span>
          </span>
        )}
        {store.sets.length > 0 && (
          <select
            value={selectedSetId ?? ''}
            onChange={(e) => (e.target.value ? applySetById(e.target.value) : setSelectedSetId(null))}
            className="px-2.5 py-1.5 text-sm bg-white border border-line rounded outline-none focus:border-green"
          >
            <option value="">— Choose a saved set —</option>
            {store.sets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{store.defaultId === s.id ? ' (default)' : ''}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={saveCurrentAsNew}
          className="px-2.5 py-1.5 text-xs font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
        >
          Save current…
        </button>
        {selectedSetId && store.sets.some((s) => s.id === selectedSetId) && (
          <>
            <button
              type="button"
              onClick={updateSelectedSet}
              title="Overwrite the selected set with the current filters"
              className="px-2.5 py-1.5 text-xs font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            >
              Update
            </button>
            <button
              type="button"
              onClick={() => toggleDefault(selectedSetId)}
              title="Apply this set automatically when the Events list opens"
              className={
                'px-2.5 py-1.5 text-xs font-semibold rounded border transition-colors ' +
                (store.defaultId === selectedSetId
                  ? 'border-brass text-brass bg-brass-soft'
                  : 'border-line text-ink-soft hover:text-brass hover:border-brass')
              }
            >
              {store.defaultId === selectedSetId ? '★ Default' : '☆ Set default'}
            </button>
            <button
              type="button"
              onClick={() => deleteSet(selectedSetId)}
              className="px-2.5 py-1.5 text-xs font-semibold rounded border border-line text-ink-soft hover:text-oxblood hover:border-oxblood transition-colors"
            >
              Delete
            </button>
          </>
        )}
      </div>

      <div className="bg-white border border-line rounded">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-ink-soft hover:text-ink transition-colors"
        >
          <Icon name="filter" size={14} />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-brass text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
              {activeFilterCount}
            </span>
          )}
          <span className="flex-1" />
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setFilters(EMPTY_FILTERS)
              }}
              className="text-xs text-ink-soft hover:text-oxblood transition-colors"
            >
              Clear
            </button>
          )}
          <Icon
            name="chevronDown"
            size={14}
            className={`transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {filtersOpen && (
          <FiltersPanel
            types={types ?? []}
            users={users ?? []}
            filters={filters}
            onChange={setFilters}
          />
        )}
      </div>

      <div className="flex gap-1 border-b border-line">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 text-sm transition-colors ${
              statusFilter === s
                ? 'border-b-2 border-green text-green font-semibold -mb-px'
                : 'text-ink-soft hover:text-ink border-b-2 border-transparent'
            }`}
          >
            {statusTabLabel(s)} {s !== 'All' && events && (
              <span className="ml-1 text-xs text-ink-soft">
                ({events.filter((e) => effectiveStatus(e, todayIso) === s).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading events...</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load events</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {events && filtered.length === 0 && statusFilter !== 'Idea' && (
        <div className="bg-white border border-line rounded p-8 text-center text-ink-soft">
          No events match the current filter.
        </div>
      )}

      {events && filtered.length === 0 && statusFilter === 'Idea' && (
        <div className="bg-white border border-line rounded p-8 text-center text-ink-soft">
          <div className="font-semibold text-ink mb-1">No ideas yet</div>
          <div className="text-sm mb-4">Model a candidate event for viability before committing it to the plan.</div>
          <button
            type="button"
            onClick={() => setCreatingIdea(true)}
            className="px-4 py-2 text-sm font-semibold rounded bg-brass text-white hover:bg-brass/90 transition-colors"
          >
            + New idea
          </button>
        </div>
      )}

      {filtered.length > 0 && statusFilter === 'Idea' && (
        <IdeasGrid ideas={filtered} />
      )}

      {filtered.length > 0 && statusFilter !== 'Idea' && view === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((ev) => (
            <EventCard key={ev.id} event={ev} merac={meracSummary?.[ev.id]} />
          ))}
        </div>
      )}

      {filtered.length > 0 && statusFilter !== 'Idea' && view === 'list' && (
        <EventTable events={filtered} merac={meracSummary} />
      )}

      {creatingNew && <NewEventModal onClose={() => setCreatingNew(false)} />}
      {creatingIdea && <NewEventModal asIdea onClose={() => setCreatingIdea(false)} />}
      {merging && <MergeDuplicatesModal groups={duplicateGroups} onClose={() => setMerging(false)} />}
    </div>
  )
}

// ---- Ideas tab (Forecast Studio entry) ------------------------------------
// Idea cards link into Forecast Studio. Rendered with the forecast primitives
// for parity with the reference prototype (EM-1).

function IdeasGrid({ ideas }: { ideas: EventListItem[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16 }}>
      {ideas.map((e) => <IdeaCard key={e.id} e={e} />)}
    </div>
  )
}

function IdeaCard({ e }: { e: EventListItem }) {
  const nav = useNavigate()
  return (
    <Card style={{ cursor: 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Pill fg={e.type.colourText} bg={e.type.colourBg}>{e.type.name}</Pill>
        <Pill fg={C.brass} bg={C.brassSoft}>Idea</Pill>
        <span style={{ marginLeft: 'auto', fontFamily: F.mono, fontSize: 11, color: C.inkFaint }}>
          {formatEventDates(e.dates, e.startDate, e.endDate)}
        </span>
      </div>
      <div style={{ fontFamily: F.serif, fontSize: 19, fontWeight: 700, color: C.ink, lineHeight: 1.15 }}>{e.name}</div>
      <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginTop: 6, lineHeight: 1.5, minHeight: 38 }}>
        {e.description || 'A candidate event being modelled for viability.'}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.lineSoft}` }}>
        <div>
          <div style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: C.inkFaint }}>Viability</div>
          <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginTop: 2 }}>Model in Forecast Studio</div>
        </div>
        <Btn variant="soft" small icon="chart" onClick={() => nav(`/forecast/${e.id}`)}>Open in Studio</Btn>
      </div>
    </Card>
  )
}

// ---- Merge duplicates modal (item 8) --------------------------------------

function MergeDuplicatesModal({ groups, onClose }: { groups: EventListItem[][]; onClose: () => void }) {
  const merge = useMergeEvents()
  const [done, setDone] = useState<Set<string>>(new Set())

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-12 p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl">Merge duplicate events</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink text-xl">×</button>
        </div>
        <p className="text-sm text-ink-soft mb-4">
          These events share a name. Merging folds the others' dates into the earliest one
          (kept as the single card) and archives the rest. The kept event's roster, actions
          and other details are unchanged.
        </p>
        <div className="space-y-3">
          {groups.map((g) => {
            const primary = g[0]!
            const rest = g.slice(1)
            const key = primary.name.trim().toLowerCase()
            const isDone = done.has(key)
            return (
              <div key={key} className="border border-line rounded p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{primary.name}</div>
                    <div className="text-xs text-ink-soft mt-0.5">
                      {g.length} events · keep{' '}
                      <span className="font-mono">{formatEventDates(primary.dates, primary.startDate, primary.endDate)}</span>
                      {' '}+ fold in{' '}
                      <span className="font-mono">{rest.map((e) => formatEventDates(e.dates, e.startDate, e.endDate)).join('; ')}</span>
                    </div>
                  </div>
                  {isDone ? (
                    <span className="text-xs font-semibold text-green whitespace-nowrap pt-1">✓ Merged</span>
                  ) : (
                    <button
                      type="button"
                      disabled={merge.isPending}
                      onClick={() => merge.mutate(
                        { primaryId: primary.id, mergeIds: rest.map((e) => e.id) },
                        { onSuccess: () => setDone((d) => new Set(d).add(key)) }
                      )}
                      className="px-3 py-1.5 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50 whitespace-nowrap"
                    >
                      {merge.isPending ? 'Merging…' : 'Merge into one'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        {merge.isError && (
          <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs mt-3">
            {(merge.error as Error).message}
          </div>
        )}
        <div className="flex justify-end mt-5 pt-4 border-t border-line">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment">Done</button>
        </div>
      </div>
    </div>
  )
}

// ---- Filters panel --------------------------------------------------------

function FiltersPanel({
  types, users, filters, onChange
}: {
  types: EventType[]
  users: UserOption[]
  filters: Filters
  onChange: (f: Filters) => void
}) {
  function toggleType(id: number) {
    const next = new Set(filters.typeIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange({ ...filters, typeIds: next })
  }

  return (
    <div className="border-t border-line px-4 py-4 space-y-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">
          Type
        </div>
        <div className="flex flex-wrap gap-1.5">
          {types.map((t) => {
            const active = filters.typeIds.has(t.id)
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleType(t.id)}
                className="px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors"
                style={
                  active
                    ? { background: t.colour_bg, color: t.colour_text, borderColor: t.colour_border }
                    : { background: 'transparent', borderColor: '#d8d2c4', color: '#5a5a5a' }
                }
              >
                {t.name}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft block mb-1">
            Manager
          </span>
          <select
            value={filters.managerId}
            onChange={(e) => {
              const v = e.target.value
              onChange({
                ...filters,
                managerId: v === 'all' ? 'all' : Number(v)
              })
            }}
            className="w-full px-2.5 py-1.5 text-sm bg-white border border-line rounded outline-none focus:border-green"
          >
            <option value="all">Any manager</option>
            <option value="0">— No manager assigned —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name ?? `User ${u.id}`}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft block mb-1">
            From date
          </span>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => onChange({ ...filters, fromDate: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm bg-white border border-line rounded outline-none focus:border-green"
          />
        </label>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-soft block mb-1">
            To date
          </span>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => onChange({ ...filters, toDate: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm bg-white border border-line rounded outline-none focus:border-green"
          />
        </label>
      </div>
    </div>
  )
}

// ---- View toggle ----------------------------------------------------------

function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <div className="inline-flex border border-line rounded overflow-hidden">
      <button
        type="button"
        aria-pressed={view === 'grid'}
        aria-label="Grid view"
        title="Grid view"
        onClick={() => onChange('grid')}
        className={
          'px-2.5 py-2 text-sm transition-colors ' +
          (view === 'grid' ? 'bg-green text-parchment' : 'bg-white text-ink-soft hover:text-ink')
        }
      >
        <Icon name="grid" size={14} />
      </button>
      <button
        type="button"
        aria-pressed={view === 'list'}
        aria-label="List view"
        title="List view"
        onClick={() => onChange('list')}
        className={
          'px-2.5 py-2 text-sm transition-colors border-l border-line ' +
          (view === 'list' ? 'bg-green text-parchment' : 'bg-white text-ink-soft hover:text-ink')
        }
      >
        <Icon name="list" size={14} />
      </button>
    </div>
  )
}

// ---- Cards ----------------------------------------------------------------

function EventCard({ event, merac }: { event: EventListItem; merac?: EventMeracSummary }) {
  const isArchived = event.archivedAt != null
  return (
    <Link
      to={`/events/${event.id}`}
      className={
        'block bg-white border border-line rounded overflow-hidden transition-shadow hover:shadow-md ' +
        (isArchived ? 'opacity-60' : '')
      }
    >
      <div className="h-1.5" style={{ background: event.type.colourBorder }} />
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className={'text-lg font-semibold leading-tight flex-1 ' + (isArchived ? 'line-through' : '')}>
            {event.name}
          </h3>
          {isArchived
            ? (
              <span className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide bg-ink-soft/15 text-ink-soft whitespace-nowrap">
                Archived
              </span>
            )
            : <StatusBadge status={event.status} />
          }
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap flex-shrink-0"
            style={{
              background: event.type.colourBg,
              color: event.type.colourText
            }}
          >
            {event.type.name}
          </span>
          <span className="text-ink-soft font-mono text-xs truncate">
            {formatEventDates(event.dates, event.startDate, event.endDate)}
          </span>
        </div>
        {event.location && (
          <div className="text-xs text-ink-soft">{event.location}</div>
        )}
        <div>
          <div className="flex items-center justify-between text-xs text-ink-soft mb-1">
            <span>Build</span>
            <span className="font-mono">{event.progress}%</span>
          </div>
          <div className="h-1.5 bg-parchment rounded overflow-hidden">
            <div
              className="h-full bg-green transition-all"
              style={{ width: `${clampProgress(event.progress)}%` }}
            />
          </div>
        </div>
        <MeracBadge code={event.meracEventCode} grossPence={event.meracGrossPence ?? null} merac={merac} />
        <div className="flex items-center justify-between pt-1 border-t border-line">
          <ManagerAvatar manager={event.manager} />
        </div>
      </div>
    </Link>
  )
}

// ---- Table (list view) ----------------------------------------------------

function EventTable({ events, merac }: { events: EventListItem[]; merac?: MeracSummaryMap }) {
  return (
    <div className="bg-white border border-line rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ink-soft border-b border-line">
            <th className="px-3 py-2 font-semibold w-1"></th>
            <th className="px-3 py-2 font-semibold">Event</th>
            <th className="px-3 py-2 font-semibold">Type</th>
            <th className="px-3 py-2 font-semibold">Dates</th>
            <th className="px-3 py-2 font-semibold">Manager</th>
            <th className="px-3 py-2 font-semibold w-32">Build</th>
            <th className="px-3 py-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const isArchived = e.archivedAt != null
            const m = merac?.[e.id]
            return (
            <tr
              key={e.id}
              className={
                'border-b border-line last:border-b-0 hover:bg-parchment transition-colors ' +
                (isArchived ? 'opacity-60' : '')
              }
            >
              <td className="pl-0 pr-0 py-3" style={{ width: 4 }}>
                <div className="w-1 h-8 rounded-r" style={{ background: e.type.colourBorder }} />
              </td>
              <td className="px-3 py-3">
                <Link
                  to={`/events/${e.id}`}
                  className={'font-semibold hover:text-green ' + (isArchived ? 'line-through' : '')}
                >
                  {e.name}
                </Link>
                {(e.meracEventCode || m?.hasData) && <MeracInline code={e.meracEventCode} merac={m} />}
                {e.location && (
                  <div className="text-xs text-ink-soft mt-0.5">{e.location}</div>
                )}
              </td>
              <td className="px-3 py-3">
                <span
                  className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{ background: e.type.colourBg, color: e.type.colourText }}
                >
                  {e.type.name}
                </span>
              </td>
              <td className="px-3 py-3 font-mono text-xs text-ink-soft whitespace-nowrap">
                {formatEventDates(e.dates, e.startDate, e.endDate)}
              </td>
              <td className="px-3 py-3">
                <ManagerAvatar manager={e.manager} />
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-parchment rounded overflow-hidden min-w-[60px]">
                    <div className="h-full bg-green" style={{ width: `${clampProgress(e.progress)}%` }} />
                  </div>
                  <span className="text-xs font-mono text-ink-soft">{e.progress}%</span>
                </div>
              </td>
              <td className="px-3 py-3">
                {isArchived
                  ? <span className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide bg-ink-soft/15 text-ink-soft">Archived</span>
                  : <StatusBadge status={e.status} />
                }
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


// ---- Shared bits ----------------------------------------------------------

function StatusBadge({ status, archivedAt }: { status: EventStatus; archivedAt?: string | null }) {
  if (archivedAt) {
    return (
      <span className="px-2 py-0.5 text-xs font-semibold border rounded bg-ink-soft text-parchment border-ink-soft whitespace-nowrap">
        Archived
      </span>
    )
  }
  const styles: Record<EventStatus, string> = {
    'Idea':            'bg-brass-soft text-brass border-brass',
    'Draft':           'bg-parchment text-ink-soft border-line',
    'In Development':  'bg-brass-soft text-brass border-brass',
    'Live':            'bg-oxblood-soft text-oxblood border-oxblood',
    'Complete':        'bg-green-soft text-green border-green',
    'Cancelled':       'bg-parchment text-ink-soft border-line'
  }
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold border rounded ${styles[status]} whitespace-nowrap`}>
      {status}
    </span>
  )
}

function ManagerAvatar({ manager }: { manager: EventListItem['manager'] }) {
  if (!manager) {
    return <span className="text-xs text-ink-soft italic">No manager assigned</span>
  }
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-full bg-green text-parchment text-xs font-semibold flex items-center justify-center">
        {manager.avatarInitials ?? '?'}
      </div>
      <span className="text-xs text-ink-soft">{manager.name}</span>
    </div>
  )
}

// ---- MERAC sales badge ----------------------------------------------------
// Shows on events with synced MERAC ticket data. Train + £ icons signal
// "MERAC sales data present"; ticket count + "refreshed X ago" always show;
// the £ total only renders for finance users (server nulls grossPence
// otherwise). bySource separates the streams (MERAC advance bookings vs
// QuayTickets online) in the tooltip + totals.
//
// NOTE: this is sales booked AGAINST the event's MERAC session — advance /
// booked income. Gate / walk-up till takings on event days aren't attributed
// to the event in MERAC, so they're not included here.

function gbp(pence: number): string {
  return '£' + Math.round(pence / 100).toLocaleString('en-GB')
}

const MERAC_SOURCE_LABEL: Record<string, string> = {
  merac: 'MERAC',
  quaytickets: 'QuayTickets'
}

function meracTitle(merac: EventMeracSummary): string {
  const lines = ['MERAC sales data (advance / booked)']
  for (const s of merac.bySource) {
    const name = MERAC_SOURCE_LABEL[s.source] ?? s.source
    const money = s.grossPence != null ? ` · ${gbp(s.grossPence)}` : ''
    lines.push(`  ${name}: ${s.ticketCount.toLocaleString('en-GB')} tickets${money}`)
  }
  if (merac.netPence != null) lines.push(`Net (after refunds): ${gbp(merac.netPence)}`)
  if (merac.lastRefreshed) lines.push(`Refreshed ${new Date(merac.lastRefreshed).toLocaleString('en-GB')}`)
  return lines.join('\n')
}

function MeracBadge({ code, grossPence, merac }: { code: string | null; grossPence: number | null; merac?: EventMeracSummary }) {
  // Show whenever the event is MERAC-linked — `code` (meracEventCode) is on the
  // card data from the main events API, so the badge never depends on the
  // separate summary call. Figures (£ / tickets / refreshed) enrich in when the
  // summary has loaded; if it hasn't, the icons + label still show.
  if (!code && !merac?.hasData && grossPence == null) return null
  // Current Bookings £ comes from the events-list response (grossPence) so it
  // rides the same request as the badge and can't desync; fall back to the
  // summary if the list read didn't carry it.
  const gross = grossPence ?? merac?.grossPence ?? null
  const title = merac?.hasData ? meracTitle(merac) : `MERAC-linked${code ? ` (${code})` : ''}`
  return (
    <div className="mt-1 rounded-md border border-green/40 bg-green-soft/60 px-2.5 py-2" title={title}>
      <div className="flex items-center gap-2 text-green flex-wrap">
        <Icon name="train" size={16} />
        <Icon name="pound" size={16} />
        <span className="text-xs font-bold uppercase tracking-wide">MERAC data</span>
        {/* Current bookings total (gross £) inline after the label. Only shown
            to financial users — grossPence is null on the wire otherwise. */}
        {gross != null && (
          <span className="text-xs font-semibold normal-case">
            Current Bookings {gbp(gross)}
          </span>
        )}
        {merac?.lastRefreshed && (
          <span className="ml-auto text-[10px] font-mono text-ink-soft normal-case">
            {relativeTime(merac.lastRefreshed)}
          </span>
        )}
      </div>
      {merac?.hasData && merac.ticketCount > 0 && (
        <div className="mt-1 text-sm text-ink-soft">
          {merac.ticketCount.toLocaleString('en-GB')} tickets
        </div>
      )}
    </div>
  )
}

// Compact MERAC indicator for the dense list/table view — train + £ icons
// with the full summary in the tooltip.
function MeracInline({ code, merac }: { code: string | null; merac?: EventMeracSummary }) {
  if (!code && !merac?.hasData) return null
  return (
    <span
      className="inline-flex items-center gap-0.5 ml-1.5 align-middle text-green"
      title={merac?.hasData ? meracTitle(merac) : `MERAC-linked${code ? ` (${code})` : ''}`}
    >
      <Icon name="train" size={13} />
      <Icon name="pound" size={13} />
    </span>
  )
}

// Compact "2h ago" / "3d ago" relative time for the last-refreshed stamp.
// Absolute timestamp lives in the badge's title attribute.
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.round(days / 30)}mo ago`
}
